/* eslint-disable react/prop-types */
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import DuplicateIcon from "@mui/icons-material/ContentCopy";
import UpIcon from "@mui/icons-material/ArrowUpward";
import DownIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";

import Comments from "../Collab/Comments";
import AddTag from "../AddTag/AddTag";
import ConfirmModal from "../modals/confirmModal";

const columnOptions = [1, 2, 3];

const ComponentToolbar = ({ viewId, slides, component, currentUser, onUpdate }) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(null);
  const [selectedSlide, setSelectedSlide] = useState(null);

  const getComponentSlideId = () => {
    let slideId = null;
    slides?.map((slide) => {
      // console.log("slide.components", slide.components, component?.id);
      if (slide.components?.includes(component?.id)) {
        slideId = slide.id;
      }
    });
    // console.log("slideId", slideId);
    return slideId;
  };

  const openMenu = () => {
    setIsSettingsModalOpen(true);
    // Optionally reset selections when toggling the menu
    setSelectedColumns(component?.cards);
    setSelectedSlide(getComponentSlideId());
  };

  const closeMenu = () => {
    setIsSettingsModalOpen(false);
    // Optionally reset selections when toggling the menu
    setSelectedColumns(component.cards);
    setSelectedSlide(getComponentSlideId());
  };

  const handleSaveComponent = async () => {
    if (!selectedColumns) return;

    const docRef = doc(db, "views", viewId); // Ensure you have a valid document reference
    let contentsArray = [];

    // Update the component with new settings
    try {
      const docSnap = await getDoc(docRef); // Get the current document snapshot
      if (docSnap.exists()) {
        // If document exists, retrieve the existing details
        const data = docSnap.data();
        contentsArray = data.contents ?? [];
      } else {
        console.error("Error saving component to Firestore. View not found:", viewId);
        return false;
      }

      // console.log("initial contentsArray", contentsArray);

      // Integrate updated component into contentsArray
      // console.log("component.id", component?.id);
      const updatedComponent = contentsArray.find((c) => c.id === component?.id);
      updatedComponent.cards = selectedColumns;
      const updatedContents = contentsArray.map((c) => (c.id === component?.id ? updatedComponent : c));

      // console.log("updated updatedComponents", updatedContents);

      // Set the document with updated details
      await updateDoc(docRef, { contents: updatedContents });
      console.log("Components updated successfully in Firestore.");
    } catch (error) {
      console.error("Error updating components in Firestore:", error);
    }

    // Update the slides if the component was moved
    if (selectedSlide !== getComponentSlideId()) {
      const docRef = doc(db, "views", viewId);
      try {
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          let existingSlides = docSnap.data().slides || [];
          let updatedSlides = existingSlides.map((slide) => {
            const updatedSlide = slide;
            const components = slide.components?.length > 0 ? slide.components.filter((c) => c !== component.id) : [];
            updatedSlide.components = components;
            return updatedSlide;
          });

          const index = updatedSlides.findIndex((slide) => slide.id === selectedSlide);
          if (index !== -1) {
            updatedSlides[index].components.push(component.id);
          } else {
            updatedSlides.push({ id: selectedSlide, components: [component.id] });
          }

          await updateDoc(docRef, { slides: updatedSlides });
          console.log("Slides updated successfully in Firestore.");
        } else {
          console.error("Document does not exist.");
        }
      } catch (error) {
        console.error("Error updating slides in Firestore:", error);
      }
    } else {
      console.log("Component was not moved to a different slide.");
    }

    closeMenu();

    onUpdate();
  };

  const handleMoveUpComponent = async () => {
    const docRef = doc(db, "views", viewId);
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const contentsArray = data.contents || [];
        const slidesArray = data.slides || [];

        const currentIndex = contentsArray.findIndex(c => c.id === component.id);

        if (currentIndex > 0) {
          // Move in contents array
          const newContentsArray = [...contentsArray];
          const temp = newContentsArray[currentIndex];
          newContentsArray[currentIndex] = newContentsArray[currentIndex - 1];
          newContentsArray[currentIndex - 1] = temp;

          // Move in slides array
          const slideId = getComponentSlideId();
          const newSlidesArray = slidesArray.map(slide => {
            if (slide.id === slideId) {
              const componentIndexInSlide = slide.components.indexOf(component.id);
              if (componentIndexInSlide > 0) {
                const newSlideComponents = [...slide.components];
                const slideTemp = newSlideComponents[componentIndexInSlide];
                newSlideComponents[componentIndexInSlide] = newSlideComponents[componentIndexInSlide - 1];
                newSlideComponents[componentIndexInSlide - 1] = slideTemp;
                return { ...slide, components: newSlideComponents };
              }
            }
            return slide;
          });

          await updateDoc(docRef, { contents: newContentsArray, slides: newSlidesArray });
          onUpdate("move");
        }
      }
    } catch (error) {
      console.error("Error moving component up:", error);
    }
  };

  const handleMoveDownComponent = async () => {
    const docRef = doc(db, "views", viewId);
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const contentsArray = data.contents || [];
        const slidesArray = data.slides || [];

        const currentIndex = contentsArray.findIndex(c => c.id === component.id);

        if (currentIndex < contentsArray.length - 1 && currentIndex !== -1) {
          // Move in contents array
          const newContentsArray = [...contentsArray];
          const temp = newContentsArray[currentIndex];
          newContentsArray[currentIndex] = newContentsArray[currentIndex + 1];
          newContentsArray[currentIndex + 1] = temp;

          // Move in slides array
          const slideId = getComponentSlideId();
          const newSlidesArray = slidesArray.map(slide => {
            if (slide.id === slideId) {
              const componentIndexInSlide = slide.components.indexOf(component.id);
              if (componentIndexInSlide < slide.components.length - 1 && componentIndexInSlide !== -1) {
                const newSlideComponents = [...slide.components];
                const slideTemp = newSlideComponents[componentIndexInSlide];
                newSlideComponents[componentIndexInSlide] = newSlideComponents[componentIndexInSlide + 1];
                newSlideComponents[componentIndexInSlide + 1] = slideTemp;
                return { ...slide, components: newSlideComponents };
              }
            }
            return slide;
          });

          await updateDoc(docRef, { contents: newContentsArray, slides: newSlidesArray });
          onUpdate("move");
        }
      }
    } catch (error) {
      console.error("Error moving component down:", error);
    }
  };

  const handleDuplicateComponent = async () => {
    // console.log("Duplicating component:", component);
    const docRef = doc(db, "views", viewId);
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const contentsArray = data.contents || [];
        const slidesArray = data.slides || [];

        const componentToDuplicate = contentsArray.find(c => c.id === component.id);
        if (!componentToDuplicate) {
          console.error("Component to duplicate not found");
          return;
        }

        const newComponent = {
          ...componentToDuplicate,
          id: uuidv4(),
          comments: [], // Start with fresh comments
        };

        const originalIndex = contentsArray.findIndex(c => c.id === component.id);
        const newContentsArray = [
          ...contentsArray.slice(0, originalIndex + 1),
          newComponent,
          ...contentsArray.slice(originalIndex + 1),
        ];

        const slideId = getComponentSlideId();
        const newSlidesArray = slidesArray.map(slide => {
          if (slide.id === slideId) {
            const originalComponentIndexInSlide = slide.components.indexOf(component.id);
            const newSlideComponents = [
              ...slide.components.slice(0, originalComponentIndexInSlide + 1),
              newComponent.id,
              ...slide.components.slice(originalComponentIndexInSlide + 1),
            ];
            return { ...slide, components: newSlideComponents };
          }
          return slide;
        });

        await updateDoc(docRef, { contents: newContentsArray, slides: newSlidesArray });
        console.log("Component duplicated successfully!");
        onUpdate("duplicate");
      }
    } catch (error) {
      console.error("Error duplicating component:", error);
    }
  };

  const handleDeleteComponent = async () => {
    // console.log("Deleting component:", component);
    const docRef = doc(db, "views", viewId);
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        let existingComponents = docSnap.data().contents || [];
        let filteredComponents = existingComponents.filter((c) => {
          // console.log("componentToDelete", componentToDelete, component.id);
          return component?.id !== c.id;
        });
        // console.log("existingComponents", existingComponents);
        // console.log("filteredComponents", filteredComponents);

        let existingSlides = docSnap.data().slides || [];
        let filteredSlides = existingSlides.map((slide) => {
          // Defensive: if slide.components is undefined, treat as empty array
          const updatedSlide = { ...slide };
          const components = Array.isArray(slide.components) ? slide.components.filter((c) => component?.id !== c) : [];
          updatedSlide.components = components;
          return updatedSlide;
        });

        await updateDoc(docRef, {
          contents: filteredComponents,
          slides: filteredSlides,
        });

        console.log("Component deleted successfully!");

        onUpdate("delete");
      } else {
        console.error("Document does not exist.");
      }
    } catch (error) {
      console.error("Error deleting component:", error);
    }

    setIsDeleteModalOpen(false);
  };

  return (
    <div className="component-toolbar">
      <Comments
        key={`Comments-tags${component.comments?.length}`}
        className="component-comments"
        viewId={viewId}
        componentId={component.id}
        currentUser={currentUser}
      />
      <div className="component-tags">
        <AddTag
          key={`AddTag-tags${component.tags?.length}`}
          viewId={viewId}
          componentId={component.id}
          initialTags={component.tags}
          onUpdate={() => {
            return onUpdate("add-tag");
          }}
        />
      </div>
      <div className="component-toolbar-buttons">
        <button className="settingsBtn" onClick={handleMoveUpComponent}>
          <UpIcon />
        </button>
        <button className="settingsBtn" onClick={handleMoveDownComponent}>
          <DownIcon />
        </button>
        <button className="settingsBtn" onClick={handleDuplicateComponent}>
          <DuplicateIcon />
        </button>

        <button className="settingsBtn" onClick={openMenu}>
          <EditIcon />
        </button>
      </div>
      {isSettingsModalOpen && (
        <div className="custom-menu-content-popout" onClick={closeMenu}>
          <div className="custom-menu" onClick={(e) => e.stopPropagation()}>
            <h3>Edit your card</h3>
            <div className="column-selection">
              <p className="menuTxt">How many columns?</p>
              {columnOptions.map((columns) => (
                <button key={columns} onClick={() => setSelectedColumns(columns)} className={`btn ${selectedColumns === columns ? "active" : ""}`}>
                  {columns} Column{columns > 1 ? "s" : ""}
                </button>
              ))}
            </div>
            <div className="slide-selection">
              <p className="menuTxt">Move to a different board</p>
              {slides.map((slide, index) => (
                <button key={slide.id} onClick={() => setSelectedSlide(slide.id)} className={`btn ${selectedSlide === slide.id ? "active" : ""}`}>
                  Board {index + 1}
                </button>
              ))}
            </div>
            <button className="build-element" onClick={handleSaveComponent} disabled={!selectedColumns || !selectedSlide}>
              Save card
            </button>
            <button className="btn 1" onClick={closeMenu} disabled={!selectedColumns || !selectedSlide}>
              Cancel
            </button>
            <button
              style={{ float: "right", marginTop: "10px" }}
              className="delBtn"
              onClick={() => {
                setIsDeleteModalOpen(!isDeleteModalOpen);
              }}
            >
              <DeleteIcon />
            </button>
          </div>
        </div>
      )}
         

      <ConfirmModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={handleDeleteComponent} />
    </div>
  );
};

export default ComponentToolbar;
